import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from './useAuth';
import { useSessions } from './useSessions';
import { useSettings } from './useSettings';
import { apiFetch } from '@/lib/api';
import { meldeAktivenNutzer } from '@/lib/sitzungskanal';
import type { CurrentUser, SessionSummary } from '@/types/domain';

// Der Nutzerwechsel als Logik, ohne Darstellung.
//
// Am Schreibtisch hängt er in einem Aufklappmenü in der Kopfzeile, am
// Telefon flach im Menü-Sheet der Bottom-Navigation. Zwei Oberflächen,
// aber nur ein Ablauf — sonst laufen sie auseinander, sobald jemand eine
// davon anfasst (Designprinzip 4).
//
// Genau das ist passiert (P4). Der bei K5 gebaute Zwei-Schritt — erst bei
// Nextcloud abmelden, dann hier anmelden — wurde in die MOBILE ANSICHT
// gebaut, nicht hierher. Der Schreibtisch-Weg rief weiterhin unmittelbar
// den Anmeldeablauf, und weil die installierte App ihre Nextcloud-Sitzung
// behält, kam dort ohne Zwischenschritt immer derselbe Benutzer an: am
// iPad war der Nutzerwechsel eine Sackgasse.
//
// Deshalb liegt der Ablauf jetzt hier. `hinzufuegen()` öffnet die
// Anleitung; erst deren zweiter Schritt ruft `anmeldungStarten()`. Wer
// den Ablauf ändert, ändert ihn an einer Stelle — die Ansichten tragen
// nur noch den Auslöser und das fertige Panel.

export type SitzungsWechsel = {
  me: CurrentUser | null;
  /** Alle angemeldeten Sitzungen ausser der gerade aktiven. */
  andere: SessionSummary[];
  wechseln: (username: string) => Promise<void>;
  abmelden: (modus: 'current' | 'all') => Promise<void>;
  /**
   * Öffnet die Anleitung zum Anmelden eines weiteren Nutzers. Führt NICHT
   * direkt in den Anmeldeablauf — warum nicht, steht im Kopf dieser Datei
   * und ausführlich in components/auth/NutzerHinzufuegen.tsx.
   */
  hinzufuegen: () => void;
  /**
   * Fertige Eigenschaften für <NutzerHinzufuegen>. Als Bündel, damit die
   * Ansichten `<NutzerHinzufuegen {...nutzerHinzufuegen} />` schreiben und
   * beim nächsten Griff nichts vergessen können.
   */
  nutzerHinzufuegen: {
    open: boolean;
    ncUrl: string | null;
    onClose: () => void;
    onAnmelden: () => void;
  };
};

async function apiPost(path: string, body?: Record<string, unknown>): Promise<void> {
  await apiFetch(path, {
    method: 'POST',
    body: JSON.stringify(body ?? {})
  });
}

export function useSitzungsWechsel(): SitzungsWechsel {
  const auth = useAuth();
  const sessions = useSessions();
  const qc = useQueryClient();
  const [hinzufuegenOffen, setHinzufuegenOffen] = useState(false);
  // Nur nc_url, und bewusst als eigene Abfrage: useSettings schlüsselt
  // nach der sortierten Schlüsselliste, eine gemeinsame Abfrage mit den
  // Portal-Werten hätte je Ansicht einen eigenen Cache-Eintrag. So teilen
  // Seitenspalte und Mobil-Menü ihre Portal-Abfrage, und diese hier kommt
  // in beiden Rahmen mit demselben Schlüssel aus.
  const ncSettings = useSettings(['nc_url']);

  const me = auth.data?.user ?? null;
  const activeUsername = sessions.data?.active ?? me?.nc_username ?? null;
  const andere = (sessions.data?.sessions ?? []).filter((s) => s.username !== activeUsername);

  // Nach Auth-Mutationen alles neu laden — grober Hammer, der fuer ein
  // Tablet mit Nutzerwechsel die richtige Koernung ist. Die anderen
  // Fenster erfahren es sofort ueber den Sitzungskanal; sie warten sonst
  // bis zum naechsten Fokuswechsel und schreiben derweil Aenderungen
  // einem Nutzer zu, den der Server gar nicht mehr verwendet (B88).
  async function refreshAll(neuerAktiver: string | null): Promise<void> {
    meldeAktivenNutzer(neuerAktiver);
    await qc.invalidateQueries();
  }

  async function wechseln(username: string): Promise<void> {
    await apiPost('/auth/activate.php', { username });
    await refreshAll(username);
  }

  async function abmelden(modus: 'current' | 'all'): Promise<void> {
    await apiPost('/auth/logout.php', { modus });
    if (modus === 'all') {
      meldeAktivenNutzer(null);
      window.location.assign('/login');
      return;
    }
    // Nach dem Abmelden einer von mehreren Sitzungen entscheidet der
    // Server, welche nachrueckt — welche das ist, sagt uns erst das
    // naechste auth/me. Deshalb hier null: "hat sich geaendert".
    await refreshAll(null);
  }

  function hinzufuegen(): void {
    setHinzufuegenOffen(true);
  }

  function anmeldungStarten(): void {
    setHinzufuegenOffen(false);
    // Ohne `add=1`: der Parameter wurde von login.php nie gelesen (dort
    // stehen nur `ziel` und `popup`) und hat schlicht nichts getan.
    // Gebraucht wird er auch nicht — callback.php legt die Sitzung immer
    // unter `sid_<nc_username>` ab, ist also von sich aus additiv. Ein
    // Parameter, der nichts bewirkt, führt beim nächsten Lesen in die
    // Irre.
    window.location.assign('/api/auth/login.php');
  }

  return {
    me,
    andere,
    wechseln,
    abmelden,
    hinzufuegen,
    nutzerHinzufuegen: {
      open: hinzufuegenOffen,
      ncUrl: ncSettings.data?.settings.nc_url ?? null,
      onClose: () => setHinzufuegenOffen(false),
      onAnmelden: anmeldungStarten
    }
  };
}
